import { useEffect, useState } from "react";
import { Zap, Cpu, Timer, Gauge, ShieldAlert } from "lucide-react";
import { useFeed } from "../../components/shell/AppShell";
import { useEvent } from "../../lib/queries";
import { GlassCard, SectionTag, SeverityCapsule, Pill, Spinner } from "../../components/ui";
import { EMESRT_COLOR, SEVERITY_COLOR, timeAgo } from "../../lib/format";
import { useAura, fmtClock } from "./useAura";
import AuraRadar from "./AuraRadar";

export default function Triage() {
  const feed = useFeed();
  const m = useAura();
  const [focusId, setFocusId] = useState<number | null>(null);

  // Auto-focus the newest live exception for the intervention panel.
  useEffect(() => {
    if (feed.live[0]) setFocusId(feed.live[0].id);
  }, [feed.live[0]?.id]);

  const { data: ev } = useEvent(focusId);
  const intervention = ev?.interventions?.[0];

  return (
    <div className="space-y-5">
      <div>
        <SectionTag>LIVE TRIAGE · BEHAVIOURAL INTELLIGENCE</SectionTag>
        <h1 className="text-xl font-semibold">Safety Aura — In-Cab Stream</h1>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        {/* Aura radar */}
        <GlassCard className="flex flex-col items-center">
          <SectionTag>SAFETY AURA · OPERATOR FOCUS</SectionTag>
          <AuraRadar m={m} size={260} />
          <div className="mt-3 grid w-full grid-cols-2 gap-2 text-xs">
            <Metric label="PERCLOS" value={`${m.perclos}%`} warn={m.perclos > 18} />
            <Metric label="MAR / YAWN" value={m.mar.toFixed(2)} warn={m.mar > 0.3} />
            <Metric label="HEAD YAW" value={`${m.yaw}°`} warn={Math.abs(m.yaw) > 18} />
            <Metric label="PULSE" value={`${m.pulse} bpm`} />
          </div>
          <div className="mono mt-3 w-full rounded-lg border border-aurora-teal/30 bg-aurora-teal/10 py-2 text-center text-aurora-teal">
            CLEAN-SHIFT TIMER · {fmtClock(m.cleanShiftSec)}
          </div>
        </GlassCard>

        {/* Live event stream */}
        <GlassCard>
          <SectionTag>LIVE EVENT STREAM</SectionTag>
          <div className="max-h-[460px] space-y-1.5 overflow-y-auto pr-1">
            {feed.live.map((e) => (
              <button
                key={`${e.id}-${e.start_time}`}
                onClick={() => setFocusId(e.id)}
                className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition edge-${e.severity} ${
                  focusId === e.id ? "border-aurora-cyan/50 bg-aurora-cyan/5" : "border-white/5 bg-white/[0.03] hover:bg-white/5"
                }`}
              >
                <span className="mono w-12 shrink-0 text-slatey">{timeAgo(e.start_time)}</span>
                <span className="flex-1 truncate font-medium">{e.event_type_label}</span>
                <span className="mono shrink-0" style={{ color: SEVERITY_COLOR[e.severity] }}>
                  {Math.round(e.risk_score)}%
                </span>
              </button>
            ))}
            {feed.live.length === 0 && (
              <div className="flex items-center gap-2 py-8 text-sm text-slatey">
                <Spinner label="Awaiting in-cab events…" />
              </div>
            )}
          </div>
        </GlassCard>

        {/* Intervention triggered */}
        <GlassCard edge={ev?.severity}>
          <SectionTag>INTERVENTION TRIGGERED</SectionTag>
          {!ev ? (
            <div className="py-10 text-center text-sm text-slatey">Select a live event…</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-critical/15 text-critical">
                  <Zap size={18} />
                </span>
                <div>
                  <div className="font-semibold">
                    {intervention ? intervention.kind.replace(/_/g, " ") : "Audible warning"}
                  </div>
                  <div className="text-[11px] text-slatey">{ev.event_type_label} · {ev.asset_registration}</div>
                </div>
                <span className="ml-auto"><SeverityCapsule severity={ev.severity} /></span>
              </div>

              <div className="rounded-lg border border-white/5 bg-white/5 p-2.5 text-xs leading-relaxed">
                <div className="section-tag mb-1">XAI REASONING</div>
                {intervention?.reasoning ||
                  `${ev.event_type_label} confirmed at risk ${Math.round(ev.risk_score)}%. Human classified; intervention dispatched to edge.`}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Tile icon={<Gauge size={13} />} label="CONFIDENCE" value={`${((intervention?.confidence ?? ev.xai.confidence) * 100).toFixed(1)}%`} />
                <Tile icon={<Timer size={13} />} label="LATENCY" value={`${intervention?.latency_ms ?? ev.xai.latency_ms}ms`} />
                <Tile icon={<Cpu size={13} />} label="MODEL" value={intervention?.model_version ?? ev.xai.model_version} />
                <Tile
                  icon={<ShieldAlert size={13} />}
                  label="EMESRT"
                  value={(intervention?.emesrt_level ?? ev.emesrt_level) + (intervention?.automated ? " auto" : "")}
                />
              </div>

              <div className="flex flex-wrap gap-1.5">
                <Pill color={EMESRT_COLOR[ev.emesrt_level]}>{ev.emesrt_level}</Pill>
                {ev.dead_man && <Pill color="#EF4444">dead-man</Pill>}
                {ev.audio_disabled && <Pill color="#F97316">audio off</Pill>}
                <Pill color="#2DD4BF">edge-dispatched</Pill>
              </div>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-lg border bg-white/5 p-2 text-center ${warn ? "border-accent/50" : "border-white/5"}`}>
      <div className={`mono text-base font-semibold ${warn ? "text-accent" : ""}`}>{value}</div>
      <div className="section-tag">{label}</div>
    </div>
  );
}

function Tile({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-white/5 bg-white/5 p-2">
      <div className="flex items-center gap-1 text-slatey">{icon}<span className="section-tag">{label}</span></div>
      <div className="mono mt-0.5 truncate text-xs">{value}</div>
    </div>
  );
}
